import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { withStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import { get as getCookie, set as setCookie } from 'es-cookie'
import { track } from '@amplitude/analytics-browser'
import { Icon } from '@iconify/react'
import {
  AppPage,
  Button,
  SearchField,
  SelectButton,
} from 'src/components/app-ui'
import CollectionEmptyState from 'src/components/collection-empty-state'
import CollaboratorCollectionEmptyState from 'src/components/collaborator-collection-empty-state'
import CollectionEdit from 'src/components/collection-edit'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import {
  getCollectionList,
  filterCollaborationsAction,
  setViewModeAction,
  collectionDuplicationCompleteAction,
} from 'src/reducers/collection/action'
import { collectionTranslationCompleteAction } from 'src/reducers/course-collection/action'
import { getTagListAction } from 'src/reducers/tag/action'
import {
  startConnection,
  addCollectionDuplicationCompleteListener,
  removeCollectionDuplicationCompleteListener,
  addTranslationCompleteListener,
  removeTranslationCompleteListener,
} from 'src/utils/signalR-connection/signalR-connection'
import useDebouncedCallback from 'src/hooks/useDebouncedCallback'
import useTrackerFunction from 'src/hooks/useTrackerFunction'
import {
  DEFAULT_SORT,
  SORT_OPTIONS,
  toSortParams,
} from 'src/utils/library-sort'
import listStyles from 'src/pages/courses/styles'
import PathLibraryCard from './components/path-library-card'
import ViewTabs from './components/view-tabs'
import FilterPopover from './components/filter-popover'

// URL segment <-> redux view mode
const VIEW_MODES = {
  'your-collections': 'personal',
  'shared-collections': 'shared',
}
const VIEW_SLUGS = {
  personal: 'your-collections',
  shared: 'shared-collections',
}

const Collections = ({ classes }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const { viewMode: urlViewMode } = useParams()

  const viewMode = useSelector(state => state.collection.viewMode)
  const list = useSelector(state => state.collection.list)
  const collaboratorList = useSelector(
    state => state.collection.collaboratorCollectionList,
  )
  const allTags = useSelector(state => state.tag.list)

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [tagIds, setTagIds] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const trackNewCollection = useTrackerFunction('clicked_new_collection')

  const isPersonal = viewMode === 'personal'
  const displayList = (isPersonal ? list : collaboratorList) || []

  // Keep the redux view mode in sync with the URL.
  useEffect(() => {
    if (!urlViewMode) {
      history.replace('/collections/your-collections')
      return
    }
    const next = VIEW_MODES[urlViewMode]
    if (next && next !== viewMode) dispatch(setViewModeAction(next))
  }, [urlViewMode, history, viewMode, dispatch])

  useEffect(() => {
    const redirectUrl = getCookie('redirectUrl')
    if (redirectUrl) {
      window.location.href = redirectUrl
      setCookie('redirectUrl', '')
    }
  }, [])

  useEffect(() => {
    dispatch(getTagListAction())
  }, [dispatch])

  const fetchPaths = useCallback(
    (filterText, sortOption, filterTagIds) => {
      const query = {
        filterText,
        currentPage: 0,
        pageSize: 100,
        ...toSortParams(sortOption, 'collectionName'),
        tagIds: filterTagIds,
      }
      const done = () => setLoaded(true)
      dispatch(
        viewMode === 'personal'
          ? getCollectionList(query, done)
          : filterCollaborationsAction(query, done),
      )
    },
    [dispatch, viewMode],
  )
  const debouncedFetch = useDebouncedCallback(fetchPaths, 500)

  useEffect(() => {
    setLoaded(false)
    fetchPaths('', DEFAULT_SORT, [])
  }, [fetchPaths])

  const refresh = (
    nextSearch = search,
    nextSort = sort,
    nextTagIds = tagIds,
  ) => {
    if (nextSearch === '') fetchPaths(nextSearch, nextSort, nextTagIds)
    else debouncedFetch(nextSearch, nextSort, nextTagIds)
  }

  const handleSearch = value => {
    setSearch(value)
    refresh(value)
  }

  const handleSort = value => {
    setSort(value)
    refresh(search, value)
  }

  const handleTags = nextIds => {
    track('collections_filtered_by_tags', {
      tag_count: nextIds.length,
      tag_names: nextIds
        .map(id => allTags.find(tag => tag.id === id)?.name)
        .filter(Boolean),
    })
    setTagIds(nextIds)
    refresh(search, sort, nextIds)
  }

  const handleViewChange = mode => {
    history.push(`/collections/${VIEW_SLUGS[mode]}`)
    dispatch(setViewModeAction(mode))
    setSearch('')
    setSort(DEFAULT_SORT)
    setTagIds([])
  }

  const handleCreate = () => {
    trackNewCollection()
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'New learning path',
          draggable: false,
          maxWidth: '500px',
          component: (
            <CollectionEdit
              onRefresh={() => refresh()}
              search={search}
              sortBy={sort}
            />
          ),
        }),
      ),
    )
  }

  // Background duplication / translation finish over SignalR. Handlers live
  // in refs so the listeners are registered once but see the latest state.
  const duplicationCompleteRef = useRef()
  duplicationCompleteRef.current = message => {
    if (!message) return
    dispatch(collectionDuplicationCompleteAction(message.collectionId))
    if (message.status === 'completed') {
      enqueueSnackbar('Learning path duplicated', { variant: 'success' })
    } else {
      enqueueSnackbar(`Duplication failed: ${message.error || ''}`, {
        variant: 'error',
      })
    }
    refresh()
  }

  // The translation worker fires once per course (with `courseId`) and once
  // for the whole collection at the end. Only the last one ends the overlay.
  const translationCompleteRef = useRef()
  translationCompleteRef.current = message => {
    if (!message || !message.collectionId) return
    if (message.courseId != null) {
      refresh()
      return
    }

    dispatch(collectionTranslationCompleteAction(message.collectionId))
    if (message.status === 'completed') {
      track('collection_translation_completed', {
        collection_id: message.collectionId,
      })
      enqueueSnackbar('Learning path translated', { variant: 'success' })
    } else {
      track('collection_translation_failed', {
        collection_id: message.collectionId,
        error: message.error,
      })
      enqueueSnackbar(`Translation failed: ${message.error || ''}`, {
        variant: 'error',
      })
    }
    refresh()
  }

  useEffect(() => {
    const onDuplicated = message => duplicationCompleteRef.current?.(message)
    const onTranslated = message => translationCompleteRef.current?.(message)
    startConnection().then(() => {
      addCollectionDuplicationCompleteListener(onDuplicated)
      addTranslationCompleteListener(onTranslated)
    })
    return () => {
      removeCollectionDuplicationCompleteListener(onDuplicated)
      removeTranslationCompleteListener(onTranslated)
    }
  }, [])

  const hasFilters = Boolean(search) || tagIds.length > 0

  const clearFilters = () => {
    setSearch('')
    setTagIds([])
    refresh('', sort, [])
  }

  const renderEmpty = () => {
    if (hasFilters) {
      return (
        <div className={classes.noResults}>
          <img src="/assets/404-collection.svg" alt="" />
          <div className={classes.noResultsTitle}>
            No learning path found matching your search.
          </div>
          <div className={classes.noResultsText}>
            Try adjusting your search terms or browse all learning paths.
          </div>
          <Button variant="outline" size="small" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )
    }
    return isPersonal ? (
      <CollectionEmptyState />
    ) : (
      <CollaboratorCollectionEmptyState />
    )
  }

  return (
    <AppPage
      title="Learning paths"
      subtitle="Ordered sets of courses learners follow."
      action={
        isPersonal
          ? { label: 'Create learning path', onClick: handleCreate }
          : undefined
      }
    >
      <div className={classes.list}>
        <ViewTabs value={viewMode} onChange={handleViewChange} />
        <FilterPopover
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
          selectedTagIds={tagIds}
          onChange={handleTags}
        />

        <div className={classes.toolbar}>
          <SearchField
            value={search}
            onChange={handleSearch}
            placeholder="Search learning paths..."
          />
          <div className={classes.filters}>
            {isPersonal && (
              <button
                type="button"
                className={classes.filterButton}
                style={
                  tagIds.length
                    ? { borderColor: 'rgba(1,95,251,.5)', color: '#015FFB' }
                    : undefined
                }
                onClick={event => setFilterAnchor(event.currentTarget)}
              >
                <Icon icon="tabler:filter" width={14} height={14} />
                {tagIds.length ? `Filter · ${tagIds.length}` : 'Filter'}
              </button>
            )}
            <SelectButton
              options={SORT_OPTIONS}
              value={sort}
              onChange={handleSort}
            />
          </div>
        </div>

        {displayList.length > 0 ? (
          <div className={classes.grid}>
            {displayList.map(collection => (
              <PathLibraryCard
                key={collection.id}
                collection={collection}
                collections={displayList}
                refresh={() => refresh()}
                shared={!isPersonal}
              />
            ))}
          </div>
        ) : (
          loaded && renderEmpty()
        )}
      </div>
    </AppPage>
  )
}

export default withStyles(listStyles)(Collections)
